import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FileContainerId, GroupId, ViewModelId } from 'balus-common';
import { Point } from '@view-model/models/common/basic';
import type { Size } from '@view-model/models/common/basic';
import type { Position, PositionDelta } from '@view-model/models/common/types/ui';
import { DraggableContainer } from '@view-model/models/common/components/DraggableContainer';
import { CreatedUserView } from '@view-model/models/framework/ui';
import type { DragContext } from '@model-framework/ui';
import { RightClickMenu, RightClickMenuItem } from '@framework/ui/elements/RightClickMenu';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import { useUserType } from '@framework/auth/authStatus';
import type { FileContainer } from '../domain/FileContainer';
import { FileCard } from './FileCard';
import { FileContainerMenu } from './FileContainerMenu';
import { FileContainerImageResizer } from './FileContainerImageResizer';

type Props = {
  groupId: GroupId;
  viewModelId: ViewModelId;
  fileContainer: FileContainer;
  position: Point;
  isSelected: boolean;
  showMenu: boolean;
  showCreatedUser: boolean;
  readonly: boolean;
  onClick(id: FileContainerId, e: React.MouseEvent<SVGGElement>): void;
  onDragStart(id: FileContainerId, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
  onDownload(fileContainer: FileContainer): void;
  onDelete(id: FileContainerId): void;
  onResize(id: FileContainerId, positionDelta: PositionDelta, size: Size): void;
};

export const FileContainerView = memo(
  ({
    groupId,
    viewModelId,
    fileContainer,
    position,
    isSelected,
    showMenu,
    showCreatedUser,
    readonly,
    onClick,
    onDragStart,
    onDrag,
    onDragEnd,
    onDownload,
    onDelete,
    onResize,
  }: Props) => {
    const { t } = useTranslation();
    // ファイルのゴミ箱への移動・複製はサーバーが通常ユーザーにしか許さないため、URL 共有の匿名ユーザーには削除を出さない
    const canDelete = useUserType() !== 'anonymous';
    const { activeRightClickMenuIds, setActiveRightClickMenuIds } = useRightClickMenuOpenState();
    const { id, filename, size } = fileContainer;

    const handleClick = useCallback((e: React.MouseEvent<SVGGElement>) => onClick(id, e), [onClick, id]);
    const handleDragStart = useCallback((e: React.MouseEvent<SVGGElement>) => onDragStart(id, e), [onDragStart, id]);
    const [rightClickPosition, setRightClickPosition] = useState<Position | null>(null);
    const handleRightClick = useCallback(
      (clickedPosition: Position, e: React.MouseEvent<SVGGElement>) => {
        setRightClickPosition(clickedPosition);
        onClick(id, e);
        setActiveRightClickMenuIds([id]);
        e.preventDefault();
      },
      [onClick, id, setActiveRightClickMenuIds]
    );
    const handleDownload = useCallback(() => {
      setActiveRightClickMenuIds([]);
      onDownload(fileContainer);
    }, [onDownload, fileContainer, setActiveRightClickMenuIds]);
    const handleResizeEnd = useCallback(
      (positionDelta: PositionDelta, resized: Size) => onResize(id, positionDelta, resized),
      [onResize, id]
    );
    const handleDelete = useCallback(() => {
      setActiveRightClickMenuIds([]);
      onDelete(id);
    }, [onDelete, id, setActiveRightClickMenuIds]);

    return (
      <>
        <DraggableContainer
          position={position}
          onClick={handleClick}
          onRightClick={readonly ? undefined : handleRightClick}
          onDragMoveStarted={handleDragStart}
          onDrag={onDrag}
          onDragMoveEnded={onDragEnd}
          dragHandleContent={
            <g cursor={readonly ? undefined : 'move'}>
              {fileContainer.isImage() ? (
                <>
                  <image
                    href={fileContainer.assetUrl(groupId, viewModelId, window.location.origin)}
                    width={size.width}
                    height={size.height}
                    preserveAspectRatio="xMidYMid meet"
                  />
                  {isSelected && (
                    <rect width={size.width} height={size.height} fill="none" stroke="#3b82f6" strokeWidth={2} />
                  )}
                </>
              ) : (
                <FileCard filename={filename} size={size} isSelected={isSelected} />
              )}
              {showCreatedUser && (
                <CreatedUserView userId={fileContainer.createdUserId} position={new Point(0, size.height)} />
              )}
              {fileContainer.isImage() && showMenu && !readonly && (
                <FileContainerImageResizer size={size} onResizeEnd={handleResizeEnd} />
              )}
            </g>
          }
        >
          {null}
        </DraggableContainer>

        {showMenu && !readonly && (
          <FileContainerMenu
            position={position.addXY(size.width / 2 + 32, 0)}
            onDownload={handleDownload}
            onDelete={canDelete ? handleDelete : undefined}
          />
        )}

        {!readonly && (
          <RightClickMenu
            isOpen={activeRightClickMenuIds.length === 1 && activeRightClickMenuIds[0] === id}
            mousePosition={rightClickPosition}
          >
            <RightClickMenuItem onClick={handleDownload} title={t('canvas.fileContainer.menu.download')} />
            {canDelete && <RightClickMenuItem onClick={handleDelete} title={t('common.action.delete')} />}
          </RightClickMenu>
        )}
      </>
    );
  }
);
FileContainerView.displayName = 'FileContainerView';
